/**
 * Per-turn command stream of one battle. This is the backend's replay format (`BattleRecord.log`) and the
 * data structure the lockstep relay fills on the server and on every client.
 *
 * Ticks are global battle ticks (`proc.battle_tick`, 50 Hz): monotonic over the whole battle, unlike `proc.timer`,
 * which restarts at every move. A turn owns the contiguous tick range `[startTick, endTick]`; only the team that
 * owns the turn produces input for it. `throughTick` is the last tick whose input is final (every tick in
 * `[startTick, throughTick]` without a command is an empty frame), which lets remote peers advance their simulation
 * deterministically.
 *
 * A solo battle recorded by the game (src/game/runtime/battle-input.ts) has one turn per move of the simulation:
 * turn 0 starts at tick 1 (the first tick of the battle), every later turn at the tick in which proc.new_move started
 * that move (proc.timer == 1 on it). The legacy ROUNDS format counts `proc.timer`, so the conversion needs these
 * per-move origins (`toLegacyRounds`, `fromLegacyRounds`).
 */
import { EMPTY_FRAME, type InputCommand, type InputFrame, cloneFrame, isHeldKey, isKeyOther, isOneShotFrame } from './input';
import { canonicalJson, hash64Hex } from './hash';
import { decodeLegacyTurnCommands } from './replay-codec';
import { KeyPressRecorder } from './recorder';

export interface TurnRecord {
  /** 0-based turn index. */
  turn: number;
  /** Team (1-based, as `proc.cur_team`) that owns input during this turn. */
  team: number;
  startTick: number;
  /** Last tick of the turn (inclusive); `null` while the turn is open. */
  endTick: number | null;
  /** Last tick whose input is final; `startTick - 1` when nothing is confirmed yet. */
  throughTick: number;
  commands: InputCommand[];
  /**
   * Online battles: the server closed the owner's input after this tick (a forced turn: the owner missed its deadline,
   * disconnected or forfeited). Every later tick of the turn is an empty frame; the turn ends in the tick in which the
   * simulation starts the next move, which a participant reports (session.endTurn with `forcedEnd`). Absent otherwise.
   */
  forcedAt?: number;
}

export interface CommandLogData {
  format: 'fwp-command-log';
  version: 1;
  turns: TurnRecord[];
}

/** One move of a battle as the simulation started it (proc.move_starts). */
export interface LegacyMove {
  /** Global tick in which the move started (proc.timer == 1); 1 for the first move of a battle. */
  startTick: number;
  /** Team of the move (proc.cur_team). */
  team: number;
}

/** The move structure a legacy ROUNDS record lacks (see CommandLog.fromLegacyRounds). */
export interface LegacyLayout {
  moves: readonly LegacyMove[];
  /** Last tick of the battle (default: the end of the last input of the last move, at least its start). */
  endTick?: number;
}

/** Upper bound for a tick value: 6 hours at 50 Hz. */
export const MAX_TICK = 50 * 60 * 60 * 6;

function commandEnd(c: InputCommand): number {
  return c.tick + c.repeat;
}

function isNonNegInt(n: unknown, max = MAX_TICK): n is number {
  return typeof n === 'number' && Number.isInteger(n) && n >= 0 && n <= max;
}

/** Structural validation of a single input frame (used by the protocol validators too). */
export function validateInputFrame(f: unknown, path = 'frame'): string | null {
  if (typeof f !== 'object' || f === null || Array.isArray(f)) return `${path}: expected object`;
  const o = f as Record<string, unknown>;
  if (typeof o.space !== 'boolean') return `${path}.space: expected boolean`;
  if (!isKeyOther(o.other)) return `${path}.other: unknown key`;
  for (const k of ['weapon', 'range', 'clickX', 'clickY'] as const) {
    if (o[k] !== undefined && (typeof o[k] !== 'number' || !Number.isFinite(o[k] as number) || Math.abs(o[k] as number) > 1e7)) {
      return `${path}.${k}: expected finite number`;
    }
  }
  if (o.other === 'weapon' || o.other === 'click') {
    if (o.space) return `${path}.space: must be false for ${o.other}`;
  }
  if (o.key !== undefined && !isHeldKey(o.key)) return `${path}.key: expected a keyboard key`;
  if (o.scanner !== undefined && (typeof o.scanner !== 'number' || !Number.isInteger(o.scanner) || o.scanner < 1 || o.scanner > 100)) {
    return `${path}.scanner: expected integer 1..100`;
  }
  if (o.surrender !== undefined) {
    if (o.surrender !== true) return `${path}.surrender: expected true`;
    if (o.space || o.other !== 'none' || o.key !== undefined) return `${path}.surrender: a surrender carries no keys`;
  }
  for (const k of Object.keys(o)) {
    if (!['space', 'other', 'weapon', 'range', 'clickX', 'clickY', 'key', 'scanner', 'surrender'].includes(k)) return `${path}.${k}: unknown field`;
  }
  return null;
}

function oneShotLabel(frame: InputFrame): string {
  if (frame.other === 'weapon' || frame.other === 'click') return frame.other;
  return frame.surrender ? 'surrender' : 'scanner';
}

/** Structural validation of an input command. */
export function validateInputCommand(c: unknown, path = 'command'): string | null {
  if (typeof c !== 'object' || c === null || Array.isArray(c)) return `${path}: expected object`;
  const o = c as Record<string, unknown>;
  if (!isNonNegInt(o.tick)) return `${path}.tick: expected integer 0..${MAX_TICK}`;
  if (!isNonNegInt(o.repeat, MAX_TICK)) return `${path}.repeat: expected non-negative integer`;
  const fe = validateInputFrame(o.frame, `${path}.frame`);
  if (fe) return fe;
  const frame = o.frame as InputFrame;
  if (isOneShotFrame(frame) && o.repeat !== 0) return `${path}.repeat: must be 0 for ${oneShotLabel(frame)}`;
  for (const k of Object.keys(o)) if (!['tick', 'repeat', 'frame'].includes(k)) return `${path}.${k}: unknown field`;
  return null;
}

/**
 * Checks that `commands` are ordered, non-overlapping, and fit into `[fromTick, throughTick]`, starting
 * after `afterTick` (the last tick already covered by earlier commands of the same turn).
 */
export function validateCommandSequence(
  commands: readonly InputCommand[],
  afterTick: number,
  fromTick: number,
  throughTick: number,
): string | null {
  let last = afterTick;
  for (let i = 0; i < commands.length; i++) {
    const c = commands[i];
    if (c.tick < fromTick) return `commands[${i}].tick ${c.tick} precedes the turn start ${fromTick}`;
    if (c.tick <= last) return `commands[${i}].tick ${c.tick} is not after tick ${last}`;
    if (commandEnd(c) > throughTick) return `commands[${i}] ends at ${commandEnd(c)} beyond throughTick ${throughTick}`;
    last = commandEnd(c);
  }
  return null;
}

export class CommandLog {
  readonly turns: TurnRecord[];

  private constructor(turns: TurnRecord[]) {
    this.turns = turns;
  }

  static create(firstTeam: number, startTick = 0): CommandLog {
    return new CommandLog([{ turn: 0, team: firstTeam, startTick, endTick: null, throughTick: startTick - 1, commands: [] }]);
  }

  get current(): TurnRecord {
    return this.turns[this.turns.length - 1];
  }

  /** Last tick covered by a command of the current turn (or `startTick - 1`). */
  lastCoveredTick(turn: TurnRecord = this.current): number {
    const last = turn.commands[turn.commands.length - 1];
    return last ? commandEnd(last) : turn.startTick - 1;
  }

  /**
   * Appends a batch to the current turn. Returns an error message instead of throwing so that the server can
   * map it to `VALIDATION_FAILED`.
   */
  append(turn: number, commands: readonly InputCommand[], throughTick: number): string | null {
    const cur = this.current;
    if (turn !== cur.turn) return `turn ${turn} is not the current turn ${cur.turn}`;
    if (cur.endTick !== null) return `turn ${turn} is already closed`;
    if (cur.forcedAt !== undefined) return `turn ${turn} takes no more input (forced after tick ${cur.forcedAt})`;
    if (!isNonNegInt(throughTick)) return `throughTick must be an integer 0..${MAX_TICK}`;
    if (throughTick < cur.throughTick) return `throughTick ${throughTick} moves backwards (was ${cur.throughTick})`;
    // New input can only follow the confirmed range: ticks up to throughTick are final.
    const err = validateCommandSequence(commands, Math.max(this.lastCoveredTick(cur), cur.throughTick), cur.startTick, throughTick);
    if (err) return err;
    for (const c of commands) cur.commands.push({ tick: c.tick, frame: cloneFrame(c.frame), repeat: c.repeat });
    cur.throughTick = throughTick;
    return null;
  }

  /** Closes the current turn at `tick` and opens the next one for `nextTeam`. */
  endTurn(turn: number, tick: number, nextTeam: number): string | null {
    const cur = this.current;
    if (turn !== cur.turn) return `turn ${turn} is not the current turn ${cur.turn}`;
    if (cur.endTick !== null) return `turn ${turn} is already closed`;
    if (!isNonNegInt(tick)) return `tick must be an integer 0..${MAX_TICK}`;
    if (tick < cur.startTick) return `turn end tick ${tick} precedes the turn start ${cur.startTick}`;
    if (tick < cur.throughTick) return `turn end tick ${tick} precedes confirmed tick ${cur.throughTick}`;
    if (tick < this.lastCoveredTick(cur)) return `turn end tick ${tick} precedes recorded input`;
    if (!Number.isInteger(nextTeam) || nextTeam < 1) return `invalid next team ${nextTeam}`;
    cur.endTick = tick;
    cur.throughTick = tick;
    this.turns.push({ turn: cur.turn + 1, team: nextTeam, startTick: tick + 1, endTick: null, throughTick: tick, commands: [] });
    return null;
  }

  /**
   * Closes the input of the open turn after `tick` (a forced turn, see TurnRecord.forcedAt): the later ticks of the
   * turn are empty frames until endTurn. The input through `tick` must be known (throughTick == tick; an owner drops
   * what it recorded beyond it with truncateOpenTurn first).
   */
  closeInput(turn: number, tick: number): string | null {
    const cur = this.current;
    if (turn !== cur.turn) return `turn ${turn} is not the current turn ${cur.turn}`;
    if (cur.endTick !== null) return `turn ${turn} is already closed`;
    if (cur.forcedAt !== undefined) return cur.forcedAt === tick ? null : `turn ${turn} was already forced after tick ${cur.forcedAt}`;
    if (!Number.isInteger(tick) || tick < cur.startTick - 1 || tick > MAX_TICK) return `invalid forced tick ${tick}`;
    if (cur.throughTick !== tick) return `forced after tick ${tick}, but the input is known through tick ${cur.throughTick}`;
    if (this.lastCoveredTick(cur) > tick) return `forced after tick ${tick}, but input is recorded beyond it`;
    cur.forcedAt = tick;
    return null;
  }

  /** Drops input of the open turn after `tick` (used when the backend force-ends a turn). */
  truncateOpenTurn(tick: number): void {
    const cur = this.current;
    if (cur.endTick !== null) return;
    cur.commands = cur.commands
      .filter((c) => c.tick <= tick)
      .map((c) => (commandEnd(c) > tick ? { tick: c.tick, frame: c.frame, repeat: tick - c.tick } : c));
    cur.throughTick = Math.min(cur.throughTick, tick);
  }

  /** Marks the log as finished at `tick` (closes the open turn without opening a new one). */
  close(tick: number): void {
    const cur = this.current;
    if (cur.endTick === null) {
      cur.endTick = Math.max(tick, this.lastCoveredTick(cur), cur.throughTick, cur.startTick);
      cur.throughTick = cur.endTick;
    }
  }

  /** Turn that owns `tick`, or `null` when the tick is beyond the known turns. */
  turnAt(tick: number): TurnRecord | null {
    for (let i = this.turns.length - 1; i >= 0; i--) {
      const t = this.turns[i];
      if (tick >= t.startTick) {
        if (t.endTick === null || tick <= t.endTick) return t;
        return null;
      }
    }
    return null;
  }

  /**
   * Input of `tick`: a frame, `EMPTY_FRAME` when the tick is confirmed without input, or `null` when the
   * input for this tick is not known yet.
   */
  frameAt(tick: number): InputFrame | null {
    const t = this.turnAt(tick);
    if (!t) return null;
    // A forced turn: the ticks after the closed input are empty until the turn's end is known.
    if (tick > t.throughTick) return t.forcedAt !== undefined && t.endTick === null ? { ...EMPTY_FRAME } : null;
    // Binary search for the last command with c.tick <= tick.
    let lo = 0;
    let hi = t.commands.length - 1;
    let found = -1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (t.commands[mid].tick <= tick) {
        found = mid;
        lo = mid + 1;
      } else hi = mid - 1;
    }
    if (found >= 0 && commandEnd(t.commands[found]) >= tick) return cloneFrame(t.commands[found].frame);
    return { ...EMPTY_FRAME };
  }

  /** Total number of commands. */
  get commandCount(): number {
    let n = 0;
    for (const t of this.turns) n += t.commands.length;
    return n;
  }

  toJSON(): CommandLogData {
    return {
      format: 'fwp-command-log',
      version: 1,
      turns: this.turns.map((t) => ({ ...t, commands: t.commands.map((c) => ({ tick: c.tick, frame: cloneFrame(c.frame), repeat: c.repeat })) })),
    };
  }

  /** Content hash of the log (integrity of stored replays). */
  hash(): string {
    return hash64Hex(canonicalJson(this.toJSON()));
  }

  static fromJSON(data: CommandLogData): CommandLog {
    const err = validateCommandLogData(data);
    if (err) throw new Error(`Invalid command log: ${err}`);
    return new CommandLog(
      data.turns.map((t) => ({ ...t, commands: t.commands.map((c) => ({ tick: c.tick, frame: cloneFrame(c.frame), repeat: c.repeat })) })),
    );
  }

  /**
   * Legacy ROUNDS record (proc.ROUNDS_RECORD) of the log, as proc.write_key_press writes it: the input of every tick
   * is fed, in `proc.timer` ticks of its move, to KeyPressRecorder (write_key_press semantics: key runs, weapon and
   * click records after an empty tick record, a pending run flushed by the next differing tick and never at the end),
   * and `#` starts every move of `playerTeam` but the first (proc.new_move, proc.as:980-986). The timer origin of a
   * turn is the tick before its start (turn 0: tick 0), so the turns must be the battle's moves (see the file
   * comment). The last tick of a closed log is the tick in which the battle ended (end_game_fn ran from proc.new_move
   * before the tick's input was recorded, or a surrender came before it), which the legacy recorder never saw: it
   * is left out. What the legacy format cannot express (`key`, `scanner`, `surrender`) is dropped.
   */
  toLegacyRounds(playerTeam = 1): string {
    const rec = new KeyPressRecorder();
    const none = { space: false, other: 'none' };
    const lastTurn = this.turns.length - 1;
    const endedAt = this.current.endTick;
    this.turns.forEach((t, i) => {
      if (i > 0 && t.team === playerTeam) rec.beginTurn();
      const origin = i === 0 ? 0 : t.startTick - 1;
      let end = t.endTick ?? Math.max(this.lastCoveredTick(t), t.throughTick);
      if (i === lastTurn && endedAt !== null) end = endedAt - 1;
      let c = 0;
      for (let tick = t.startTick; tick <= end; tick++) {
        while (c < t.commands.length && commandEnd(t.commands[c]) < tick) c++;
        const f = c < t.commands.length && t.commands[c].tick <= tick ? t.commands[c].frame : null;
        const timer = tick - origin;
        if (f && (f.other === 'weapon' || f.other === 'click')) {
          rec.record(timer, none);
          rec.record(timer, f);
        } else rec.record(timer, f ? { space: f.space, other: f.other } : none);
      }
    });
    return rec.toLegacyRounds();
  }

  /** The move structure of the log (one move per turn), for `fromLegacyRounds`. */
  legacyLayout(): LegacyLayout {
    const last = this.current;
    return { moves: this.turns.map((t) => ({ startTick: t.startTick, team: t.team })), endTick: last.endTick ?? Math.max(this.lastCoveredTick(last), last.throughTick) };
  }

  /**
   * Imports a legacy ROUNDS record: `#`-separated segments, one per move of `playerTeam`, with ticks counted by
   * `proc.timer` of that move.
   *
   * With `layout` (the battle's moves: start tick and team of each, e.g. proc.move_starts, and the last tick) the
   * import is exact: one turn per move, segment k is placed at the origin of the k-th move of `playerTeam`, and what
   * the legacy replay reader never sees is dropped (records beyond the end of their move: proc.new_move clears the
   * move table; records overlapped by an earlier key run: proc.read_key_press moves a run over them).
   *
   * Without a layout the moves are unknown: segment k becomes turn k of `playerTeam`, laid out right after the
   * previous one and ending one tick after its last command (the tick that flushed the recorder's last key run; the
   * last turn has one more tick, the battle's end), so no command is lost and `toLegacyRounds` returns the same text.
   * The ticks are not the battle's ticks (the other teams' moves are missing): such a log is not suitable for a
   * re-simulation.
   */
  static fromLegacyRounds(rounds: string, playerTeam = 1, layout?: LegacyLayout): CommandLog {
    const segments = rounds.split('#').map((s) => decodeLegacyTurnCommands(s));
    const turns: TurnRecord[] = [];
    const place = (cmds: InputCommand[], origin: number, from: number, to: number): InputCommand[] => {
      const out: InputCommand[] = [];
      let last = from - 1;
      for (const c of cmds) {
        const tick = origin + c.tick;
        if (tick <= last || tick > to) continue;
        const cmd = { tick, frame: cloneFrame(c.frame), repeat: Math.min(c.repeat, to - tick) };
        out.push(cmd);
        last = commandEnd(cmd);
      }
      return out;
    };
    if (layout) {
      const err = validateLegacyLayout(layout);
      if (err) throw new Error(`Invalid legacy layout: ${err}`);
      const moves = layout.moves;
      let seg = 0;
      moves.forEach((m, i) => {
        const origin = i === 0 ? 0 : m.startTick - 1;
        let end: number;
        if (i + 1 < moves.length) end = moves[i + 1].startTick - 1;
        else if (layout.endTick !== undefined) end = layout.endTick;
        else {
          const own = m.team === playerTeam ? segments[seg] ?? [] : [];
          end = Math.max(m.startTick, ...own.map((c) => origin + commandEnd(c)));
        }
        const commands = m.team === playerTeam && seg < segments.length ? place(segments[seg++], origin, m.startTick, end) : [];
        turns.push({ turn: i, team: m.team, startTick: m.startTick, endTick: end, throughTick: end, commands });
      });
      return new CommandLog(turns);
    }
    let origin = 0;
    segments.forEach((cmds, i) => {
      const lastEnd = cmds.reduce((m, c) => Math.max(m, commandEnd(c)), 0);
      const start = origin + 1;
      const end = origin + lastEnd + (i === segments.length - 1 ? 2 : 1);
      turns.push({ turn: i, team: playerTeam, startTick: start, endTick: end, throughTick: end, commands: place(cmds, origin, start, end) });
      origin = end;
    });
    return new CommandLog(turns);
  }
}

/** Checks a legacy layout: at least one move, strictly increasing start ticks, teams 1..64, endTick after the last start. */
export function validateLegacyLayout(layout: LegacyLayout): string | null {
  const moves = layout?.moves;
  if (!Array.isArray(moves) || moves.length === 0) return 'moves: expected a non-empty array';
  let prev = -1;
  for (let i = 0; i < moves.length; i++) {
    const m = moves[i];
    if (!isNonNegInt(m?.startTick) || m.startTick <= prev) return `moves[${i}].startTick: expected an integer after ${prev}`;
    if (!Number.isInteger(m.team) || m.team < 1 || m.team > 64) return `moves[${i}].team: expected integer 1..64`;
    prev = m.startTick;
  }
  if (layout.endTick !== undefined && (!isNonNegInt(layout.endTick) || layout.endTick < prev)) return `endTick: expected an integer >= ${prev}`;
  return null;
}

/** The members of a serialized log and of its turns (TurnRecord): nothing else is accepted, as for its commands. */
const LOG_KEYS: readonly string[] = ['format', 'version', 'turns'];
const TURN_KEYS: readonly string[] = ['turn', 'team', 'startTick', 'endTick', 'throughTick', 'commands', 'forcedAt'];

/** Structural and ordering validation of serialized command logs (unknown members are refused at every level). */
export function validateCommandLogData(data: unknown, maxCommands = 200_000): string | null {
  if (typeof data !== 'object' || data === null || Array.isArray(data)) return 'log: expected object';
  const d = data as Record<string, unknown>;
  for (const k of Object.keys(d)) if (!LOG_KEYS.includes(k)) return `log.${k.slice(0, 64)}: unknown field`;
  if (d.format !== 'fwp-command-log') return 'log.format: expected "fwp-command-log"';
  if (d.version !== 1) return 'log.version: expected 1';
  if (!Array.isArray(d.turns) || d.turns.length === 0) return 'log.turns: expected non-empty array';
  if (d.turns.length > 100_000) return 'log.turns: too many turns';
  let total = 0;
  let prevEnd = -1;
  for (let i = 0; i < d.turns.length; i++) {
    const p = `log.turns[${i}]`;
    const t = d.turns[i] as Record<string, unknown>;
    if (typeof t !== 'object' || t === null || Array.isArray(t)) return `${p}: expected object`;
    for (const k of Object.keys(t)) if (!TURN_KEYS.includes(k)) return `${p}.${k.slice(0, 64)}: unknown field`;
    if (t.turn !== i) return `${p}.turn: expected ${i}`;
    if (!Number.isInteger(t.team) || (t.team as number) < 1 || (t.team as number) > 64) return `${p}.team: expected integer 1..64`;
    if (!isNonNegInt(t.startTick)) return `${p}.startTick: expected integer`;
    if (i > 0 && t.startTick !== prevEnd + 1) return `${p}.startTick: expected ${prevEnd + 1}`;
    const last = i === d.turns.length - 1;
    if (t.endTick === null) {
      if (!last) return `${p}.endTick: only the last turn may be open`;
    } else if (!isNonNegInt(t.endTick) || (t.endTick as number) < (t.startTick as number)) return `${p}.endTick: invalid`;
    if (typeof t.throughTick !== 'number' || !Number.isInteger(t.throughTick) || (t.throughTick as number) < (t.startTick as number) - 1) {
      return `${p}.throughTick: invalid`;
    }
    if (t.endTick !== null && t.throughTick !== t.endTick) return `${p}.throughTick: must equal endTick for closed turns`;
    if (t.forcedAt !== undefined) {
      const f = t.forcedAt;
      if (typeof f !== 'number' || !Number.isInteger(f) || f < (t.startTick as number) - 1 || f > (t.throughTick as number)) return `${p}.forcedAt: invalid`;
      if (t.endTick === null && f !== t.throughTick) return `${p}.forcedAt: must equal throughTick for an open turn`;
    }
    if (!Array.isArray(t.commands)) return `${p}.commands: expected array`;
    total += t.commands.length;
    if (total > maxCommands) return 'log: too many commands';
    for (let j = 0; j < t.commands.length; j++) {
      const e = validateInputCommand(t.commands[j], `${p}.commands[${j}]`);
      if (e) return e;
    }
    const inputEnd = t.forcedAt !== undefined ? (t.forcedAt as number) : (t.throughTick as number);
    const seqErr = validateCommandSequence(t.commands as InputCommand[], (t.startTick as number) - 1, t.startTick as number, inputEnd);
    if (seqErr) return `${p}: ${seqErr}`;
    prevEnd = t.endTick === null ? -1 : (t.endTick as number);
  }
  return null;
}
